/**
 * AARKA Sticky ATC — Floating purchase pill
 * Shows when the main ATC button scrolls out of view.
 * Variant swatches change the variant WITHOUT scrolling.
 * Cart submit triggers the native product form for drawer support.
 */
if (!customElements.get('aarka-sticky-atc')) {
  customElements.define('aarka-sticky-atc', class extends HTMLElement {
    connectedCallback() {
      this.priceEl = this.querySelector('[data-satc-price]');
      this.btnPriceEl = this.querySelector('[data-satc-btn-price]');
      this.variantEl = this.querySelector('[data-satc-variant]');
      this.button = this.querySelector('[data-satc-button]');
      this.btnLabel = this.querySelector('.satc__btn-label');
      this.btnSep = this.querySelector('.satc__btn-sep');
      this.thumb = this.querySelector('.satc__thumb');
      this.swatches = this.querySelectorAll('.satc__swatch');
      this.variantSelect = this.querySelector('[data-satc-select]');

      this._mainButton = document.querySelector('.product-form__submit');
      this._finalCta = document.querySelector('[id$="__final_cta"]');
      this._mainForm = document.querySelector('product-form');

      this.setupObservers();
      this.setupVariantListener();
      this.setupSwatchClicks();
      this.setupDropdownChange();
      this.setupCartSubmit();
    }

    disconnectedCallback() {
      if (this._mainIO) this._mainIO.disconnect();
      if (this._ctaIO) this._ctaIO.disconnect();
      if (this._heroIO) this._heroIO.disconnect();
      document.removeEventListener('variant:changed', this._variantHandler);
    }

    /* ── Visibility logic ── */

    setupObservers() {
      var self = this;
      this._mainVisible = true;
      this._ctaVisible = false;
      this._heroVisible = true;

      if (this._mainButton) {
        this._mainIO = new IntersectionObserver(function(entries) {
          self._mainVisible = entries[0].isIntersecting;
          self.updateVisibility();
        }, { threshold: 0 });
        this._mainIO.observe(this._mainButton);
      }

      if (this._finalCta) {
        this._ctaIO = new IntersectionObserver(function(entries) {
          self._ctaVisible = entries[0].isIntersecting;
          self.updateVisibility();
        }, { threshold: 0.2 });
        this._ctaIO.observe(this._finalCta);
      }

      this._heroSection = document.querySelector('aarka-product-hero');
      if (this._heroSection) {
        this._heroIO = new IntersectionObserver(function(entries) {
          self._heroVisible = entries[0].isIntersecting;
          self.updateVisibility();
        }, { threshold: 0.15 });
        this._heroIO.observe(this._heroSection);
      } else {
        this._heroVisible = false;
      }
    }

    updateVisibility() {
      var shouldShow = !this._mainVisible && !this._ctaVisible && !this._heroVisible;
      this.classList.toggle('satc--visible', shouldShow);
    }

    /* ── Variant sync (listens to main form changes) ── */

    setupVariantListener() {
      var self = this;
      this._variantHandler = function(e) {
        var variant = e.detail?.variant;
        if (!variant) return;

        var formattedPrice = self.formatMoney(variant.price);
        if (self.priceEl) {
          self.priceEl.textContent = formattedPrice;
        }
        if (self.btnPriceEl) {
          self.btnPriceEl.textContent = formattedPrice;
        }

        if (self.variantEl) {
          self.variantEl.textContent = variant.title || '';
        }

        if (self.thumb && variant.featured_media) {
          self.thumb.src = variant.featured_media.preview_image.src.replace(/(\.\w+)(\?.*)?$/, '_80x80$1');
        }

        if (self.button) {
          self.button.disabled = !variant.available;
          if (self.btnLabel) {
            self.btnLabel.textContent = variant.available ? 'ADD TO BAG' : 'SOLD OUT';
          }
          // Show/hide price separator when sold out
          if (self.btnSep) self.btnSep.style.display = variant.available ? '' : 'none';
          if (self.btnPriceEl) self.btnPriceEl.style.display = variant.available ? '' : 'none';
        }

        self._currentVariantId = variant.id;

        // Sync swatch active state
        self.swatches.forEach(function(sw) {
          sw.classList.toggle('satc__swatch--active', sw.dataset.variantId === String(variant.id));
        });

        // Sync dropdown
        if (self.variantSelect) {
          self.variantSelect.value = String(variant.id);
        }
      };

      document.addEventListener('variant:changed', this._variantHandler);
    }

    setupDropdownChange() {
      if (!this.variantSelect) return;
      var self = this;

      this.variantSelect.addEventListener('change', function() {
        var variantId = self.variantSelect.value;
        var scrollY = window.scrollY;

        // Update main form's hidden input
        var mainFormEl = self._mainForm?.querySelector('form');
        var variantInput = mainFormEl?.querySelector('input[name="id"]');
        if (variantInput) variantInput.value = variantId;

        // Trigger main form variant radio
        var mainRadio = document.querySelector(
          '[data-section-type="main-product"] input[type="radio"][value="' + variantId + '"]'
        );
        if (mainRadio && !mainRadio.checked) {
          mainRadio.checked = true;
          mainRadio.dispatchEvent(new Event('change', { bubbles: true }));
        }

        // Prevent scroll jump
        requestAnimationFrame(function() { window.scrollTo(0, scrollY); });
      });
    }

    /* ── Swatch clicks — change variant WITHOUT scrolling ── */

    setupSwatchClicks() {
      var self = this;
      this.swatches.forEach(function(swatch) {
        swatch.addEventListener('click', function() {
          if (swatch.disabled) return;
          var variantId = swatch.dataset.variantId;

          // Save current scroll position
          var scrollY = window.scrollY;

          // Update the hidden variant input in the main product form
          var mainFormEl = self._mainForm?.querySelector('form');
          var variantInput = mainFormEl?.querySelector('input[name="id"]');
          if (variantInput) {
            variantInput.value = variantId;
          }

          // Trigger the main form's variant picker to update
          // Find matching radio in the main product section and click it
          var mainRadio = document.querySelector(
            '[data-section-type="main-product"] input[type="radio"][value="' + variantId + '"]'
          );
          if (mainRadio && !mainRadio.checked) {
            mainRadio.checked = true;
            mainRadio.dispatchEvent(new Event('change', { bubbles: true }));
          }

          // Restore scroll position after a tick (prevent scroll jump)
          requestAnimationFrame(function() {
            window.scrollTo(0, scrollY);
          });

          // Immediate visual feedback
          self.swatches.forEach(function(sw) { sw.classList.remove('satc__swatch--active'); });
          swatch.classList.add('satc__swatch--active');
        });
      });
    }

    /* ── Cart submit — trigger native product form for drawer support ── */

    setupCartSubmit() {
      var self = this;
      if (!this.button) return;

      this.button.addEventListener('click', function() {
        if (self.button.disabled) return;

        // Ensure the main form has the correct variant ID
        var mainFormEl = self._mainForm?.querySelector('form');
        var variantInput = mainFormEl?.querySelector('input[name="id"]');
        var variantId = self._currentVariantId || variantInput?.value;

        if (variantInput && self._currentVariantId) {
          variantInput.value = self._currentVariantId;
        }

        // Trigger the native product form submit
        // This handles cart drawer/notification, sections rendering, etc.
        if (mainFormEl) {
          var submitEvent = new SubmitEvent('submit', { bubbles: true, cancelable: true, submitter: self._mainButton });
          mainFormEl.dispatchEvent(submitEvent);

          // Mirror the button state from the main button
          self.setButtonState('ADDING...');
          self.button.disabled = true;

          // Watch for the main button to re-enable (cart add complete)
          var checkMainBtn = setInterval(function() {
            if (self._mainButton && !self._mainButton.classList.contains('loading')) {
              clearInterval(checkMainBtn);
              self.setButtonState('ADDED ✓');
              self.button.disabled = false;

              setTimeout(function() {
                // Re-check variant availability
                var isAvailable = self._mainButton && !self._mainButton.disabled;
                self.setButtonState(isAvailable ? 'ADD TO BAG' : 'SOLD OUT');
                self.button.disabled = !isAvailable;
              }, 1500);
            }
          }, 100);

          // Safety timeout — don't leave button stuck
          setTimeout(function() {
            clearInterval(checkMainBtn);
            if (self.btnLabel && self.btnLabel.textContent === 'ADDING...') {
              self.setButtonState('ADD TO BAG');
              self.button.disabled = false;
            }
          }, 8000);

          return;
        }

        // Fallback: direct fetch if no native form found
        if (!variantId) return;
        self.setButtonState('ADDING...');
        self.button.disabled = true;

        fetch(window.routes.cart_add_url + '.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items: [{ id: parseInt(variantId, 10), quantity: 1 }]
          })
        })
        .then(function(res) { return res.json(); })
        .then(function() {
          self.setButtonState('ADDED ✓');
          document.dispatchEvent(new CustomEvent('cart:updated'));
          setTimeout(function() {
            self.setButtonState('ADD TO BAG');
            self.button.disabled = false;
          }, 1500);
        })
        .catch(function() {
          self.setButtonState('ERROR');
          setTimeout(function() {
            self.setButtonState('ADD TO BAG');
            self.button.disabled = false;
          }, 2000);
        });
      });
    }

    setButtonState(label) {
      var isAction = (label === 'ADDING...' || label === 'ADDED ✓' || label === 'ERROR');
      if (this.btnLabel) {
        this.btnLabel.textContent = label;
      }
      // Hide price inside button during action states
      if (this.btnSep) this.btnSep.style.display = isAction ? 'none' : '';
      if (this.btnPriceEl) this.btnPriceEl.style.display = isAction ? 'none' : '';
    }

    formatMoney(cents) {
      var amount = (cents / 100).toFixed(2);
      // Use theme's money format if available
      var format = window.Shopify?.money_format || '₹{{amount}}';
      return format.replace('{{amount}}', amount).replace('{{amount_no_decimals}}', Math.round(cents / 100));
    }
  });
}
